import { Atom, atom } from 'jotai'

import { IntegrationServices } from '@sequencehq/api'
import {
  maxLength,
  required,
  validateField
} from '@sequencehq/utils/validation'

import { getRateValidators, quantityValidators } from './validators'
import { secondaryOptionsVisibleAtom } from '../atoms'
import {
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  LINE_ITEM_NAME_MAX_LENGTH
} from '../../constants'
import { RateDisplay } from 'Invoices/types'

type LineItemFormValidationResults = {
  title: string[]
  description: string[]
  quantity: string[]
  rate: string[]
  externalIds: Array<{
    key: IntegrationServices
    value: string[]
  }>
}

type CommonFormState = {
  title: string
  description: string
  quantity: string
  rate: string
  rateDisplay: RateDisplay
  externalIds: Array<{ key: IntegrationServices; value: string }>
}

/**
 * The CreateLineItem and EditLineItem form variants use the same validation rules.
 * This factory function generates validation atoms that can be used across these variants,
 * while allowing them to have different form state atoms.
 */
export const createLineItemValidatonAtoms = (
  formStateAtom: Atom<CommonFormState>
) => {
  const validationResultsAtom = atom<LineItemFormValidationResults | null>(null)

  const validateFormAtom = atom(null, (get, set) => {
    const state = get(formStateAtom)

    const rateValidators = getRateValidators(state.rateDisplay)

    const results = {
      title: validateField(state.title)([
        required,
        maxLength(
          LINE_ITEM_NAME_MAX_LENGTH,
          `Name must not exceed ${LINE_ITEM_NAME_MAX_LENGTH} characters`
        )
      ]),
      description: validateField(state.description)([
        maxLength(
          LINE_ITEM_DESCRIPTION_MAX_LENGTH,
          `Description must not exceed ${LINE_ITEM_DESCRIPTION_MAX_LENGTH} characters`
        )
      ]),
      quantity: validateField(state.quantity)(quantityValidators),
      rate: validateField(state.rate)(rateValidators),
      externalIds: state.externalIds.map(externalId => ({
        key: externalId.key,
        value: validateField(externalId.value)([required])
      }))
    }

    const secondaryOptionsValid = isSecondaryOptionsValid(results)

    if (!secondaryOptionsValid) {
      set(secondaryOptionsVisibleAtom, true)
    }

    set(validationResultsAtom, results)
    return isFormValid(results)
  })

  return { validationResultsAtom, validateFormAtom }
}

const isSecondaryOptionsValid = (
  results: LineItemFormValidationResults
): boolean => {
  return results.externalIds.every(externalId => externalId.value.length === 0)
}

const isFormValid = (results: LineItemFormValidationResults): boolean => {
  const { externalIds, ...baseFields } = results

  const baseFieldsValid = Object.values(baseFields).every(
    errors => errors.length === 0
  )
  const secondaryOptionsValid = isSecondaryOptionsValid(results)

  return [baseFieldsValid, secondaryOptionsValid].every(Boolean)
}
